// ⌘K command palette: Search (catalog + orders, in parallel), Ask AI, Actions, Navigate, Recent.
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import * as D from '@radix-ui/react-dialog'
import { defaultFilter } from 'cmdk'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft, ArrowRight, Clock, ExternalLink, FolderOpen, Keyboard, LogOut, Monitor, Moon, Package, Plus, Receipt, Search,
  Shield, Store as StoreIcon, Sun, User, Check, CornerDownLeft,
} from 'lucide-react'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command'
import { Kbd } from '@/components/ui/kbd'
import { Spinner } from '@/components/ui/spinner'
import { Avatar } from '@/components/ui/display'
import { StatusBadge } from '@/components/ui/badge'
import { cn } from '@/components/ui/cn'
import { AskAiCommand } from './command-ai'
import { looksLikeSentence, parseCatalog, parseOrders } from './palette-utils'
export { looksLikeSentence } from './palette-utils'
import { NavIcon } from './icons'
import { navigableRoutes, permissionFor, sectionFor } from './route-meta'
import { readRecents, useGuardedNavigate, useSwitchStore, type ShellKind } from './shell-context'
import { useSession } from '@/lib/session'
import { api, ApiError, qs } from '@/lib/api'
import { useDebounce } from '@/lib/hooks'
import { setThemePreference, useTheme } from '@/lib/theme'
import { money } from '@/lib/format'
import { storeNav, platformNav } from '@/app/nav'

type Page = 'root' | 'stores' | 'theme'

/** 404 = that module hasn't shipped its search yet → treat as "no results", not an error. */
async function tolerant<T>(p: Promise<T>): Promise<T | null> {
  try { return await p } catch (e) { if (e instanceof ApiError && (e.status === 404 || e.status === 403)) return null; throw e }
}

export interface CommandPaletteProps { open: boolean; onOpenChange: (o: boolean) => void; initialPage?: Page; kind: ShellKind; onShowShortcuts: () => void; onLogout: () => void }

export function CommandPalette({ open, onOpenChange, initialPage = 'root', kind, onShowShortcuts, onLogout }: CommandPaletteProps) {
  const { me, store, storeId, can } = useSession()
  const switchStore = useSwitchStore()
  const go = useGuardedNavigate()
  const theme = useTheme()
  const [page, setPage] = useState<Page>(initialPage)
  const [query, setQuery] = useState('')
  useEffect(() => { if (open) { setPage(initialPage); setQuery('') } }, [open, initialPage])
  const q = useDebounce(query.trim(), 180)
  const scope = kind === 'platform' ? 'platform' : storeId ?? 'none'
  const recents = useMemo(() => (open ? readRecents(scope) : []), [open, scope])
  const isStore = kind === 'store' || kind === 'full'

  const searchEnabled = open && page === 'root' && isStore && q.length >= 2 && !!storeId
  const catalog = useQuery({
    queryKey: ['palette-search', storeId, 'catalog', q], enabled: searchEnabled && can('products'), staleTime: 30_000, retry: false,
    queryFn: ({ signal }) => tolerant(api.get<unknown>(`/api/admin/search${qs({ q })}`, { signal })),
  })
  const orders = useQuery({
    queryKey: ['palette-search', storeId, 'orders', q], enabled: searchEnabled && (can('orders') || can('customers')), staleTime: 30_000, retry: false,
    queryFn: ({ signal }) => tolerant(api.get<unknown>(`/api/admin/orders/lookup${qs({ q })}`, { signal })),
  })
  const hits = useMemo(() => [...parseOrders(orders.data), ...parseCatalog(catalog.data)], [orders.data, catalog.data])
  const searching = searchEnabled && (catalog.isFetching || orders.isFetching)
  const searchFailed = searchEnabled && (catalog.isError || orders.isError) && !hits.length

  const close = () => onOpenChange(false)
  const run = (fn: () => void) => { close(); setTimeout(fn, 0) }
  const nav = (to: string) => run(() => void go(to))

  // Static items (actions + navigation), filtered and ranked with cmdk's scorer.
  type Item = { id: string; label: string; group: 'Actions' | 'Navigate'; icon: ReactNode; hint?: string; keywords?: string[]; shortcut?: string; onSelect: () => void; end?: ReactNode }
  const items = useMemo<Item[]>(() => {
    const out: Item[] = []
    if (isStore) {
      if (can('products')) out.push({ id: 'new-product', label: 'Create product', group: 'Actions', icon: <Plus />, keywords: ['add', 'new', 'item'], shortcut: 'c p', onSelect: () => nav('/products/new') })
      if (can('orders')) out.push({ id: 'new-order', label: 'Create order', group: 'Actions', icon: <Plus />, keywords: ['manual', 'draft', 'new'], shortcut: 'c o', onSelect: () => nav('/orders/new') })
      if (can('discounts')) out.push({ id: 'new-discount', label: 'Create discount', group: 'Actions', icon: <Plus />, keywords: ['coupon', 'code', 'sale', 'new'], shortcut: 'c d', onSelect: () => nav('/discounts/new') })
      if (can('products')) out.push({ id: 'new-collection', label: 'Create collection', group: 'Actions', icon: <Plus />, keywords: ['category', 'new'], onSelect: () => nav('/collections/new') })
      if (store?.storefrontUrl) out.push({ id: 'view-store', label: 'View store', group: 'Actions', icon: <ExternalLink />, keywords: ['storefront', 'open', 'website', 'preview'], onSelect: () => run(() => window.open(store.storefrontUrl, '_blank', 'noopener')) })
      if ((me?.stores.length ?? 0) > 1) out.push({ id: 'switch-store', label: 'Switch store…', group: 'Actions', icon: <StoreIcon />, keywords: ['change', 'shop'], onSelect: () => { setPage('stores'); setQuery('') } })
      if (me?.user.isPlatformAdmin) out.push({ id: 'platform', label: 'Open platform admin', group: 'Actions', icon: <Shield />, keywords: ['super', 'admin'], onSelect: () => nav('/platform') })
    } else if (kind === 'platform') {
      out.push({ id: 'back-stores', label: 'Back to my stores', group: 'Actions', icon: <ArrowLeft />, keywords: ['store', 'merchant'], onSelect: () => nav('/') })
    }
    out.push({ id: 'theme', label: 'Change theme…', group: 'Actions', icon: theme.resolved === 'dark' ? <Moon /> : <Sun />, keywords: ['dark', 'light', 'mode', 'appearance'], onSelect: () => { setPage('theme'); setQuery('') } })
    out.push({ id: 'theme-toggle', label: theme.resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme', group: 'Actions', icon: theme.resolved === 'dark' ? <Sun /> : <Moon />, keywords: ['dark', 'light', 'mode', 'theme'], onSelect: () => run(() => setThemePreference(theme.resolved === 'dark' ? 'light' : 'dark')) })
    out.push({ id: 'shortcuts', label: 'Keyboard shortcuts', group: 'Actions', icon: <Keyboard />, keywords: ['help', 'keys', 'hotkeys'], shortcut: '?', onSelect: () => run(onShowShortcuts) })
    out.push({ id: 'logout', label: 'Log out', group: 'Actions', icon: <LogOut />, keywords: ['sign out', 'exit'], onSelect: () => run(onLogout) })

    const navs = kind === 'platform' ? navigableRoutes('platform') : isStore ? navigableRoutes('store') : []
    const iconFor = (path: string) => {
      for (const sec of kind === 'platform' ? platformNav : storeNav) for (const it of sec.items) {
        if (it.to === path || it.children?.some(c => c.to === path) || (it.to !== '/' && path.startsWith(it.to + '/'))) return <NavIcon name={it.icon} />
      }
      return path.startsWith('/settings') ? <NavIcon name="Settings" /> : <ArrowRight />
    }
    for (const r of navs) {
      const perm = permissionFor(r.path)
      if (isStore && perm && !can(perm)) continue
      if (r.path === '/products/new' || r.path === '/orders/new' || r.path === '/discounts/new' || r.path === '/collections/new') continue
      const section = sectionFor(r.path)
      out.push({ id: `nav:${r.path}`, label: r.title, group: 'Navigate', icon: iconFor(r.path), hint: section && section !== r.title ? section : undefined, keywords: [r.path.replace(/[/-]/g, ' '), section], onSelect: () => nav(r.path) })
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStore, kind, can, store?.storefrontUrl, me, theme.resolved])

  const filtered = useMemo(() => {
    const t = query.trim()
    if (!t) return items
    return items
      .map(i => ({ i, s: defaultFilter!(i.label, t, [...(i.keywords ?? []), i.hint ?? '']) }))
      .filter(x => x.s >= 0.05)   // drop scattered fuzzy matches ("linen" ≠ "Create collection")
      .sort((a, b) => b.s - a.s)
      .map(x => x.i)
  }, [items, query])
  const actions = filtered.filter(i => i.group === 'Actions').slice(0, query ? 6 : 5)
  const navItems = filtered.filter(i => i.group === 'Navigate').slice(0, query ? 8 : 40)
  const ai = page === 'root' && isStore && looksLikeSentence(query)
  const nothing = !actions.length && !navItems.length && !hits.length && !searching && !ai

  const stores = me?.stores ?? []
  const storeMatches = stores.filter(s => !query.trim() || defaultFilter!(s.name, query.trim(), [s.slug, s.host]) > 0)
  // Keep the highlight on the first row as results arrive (cmdk otherwise keeps a stale row).
  const firstValue = page === 'root'
    ? (hits[0] ? `${hits[0].kind}:${hits[0].id}` : !query && recents[0] ? `recent:${recents[0].path}` : actions[0]?.id ?? navItems[0]?.id ?? '')
    : page === 'stores' ? (storeMatches[0] ? `store:${storeMatches[0].id}` : '') : `theme:${theme.preference}`
  const [selected, setSelected] = useState('')
  useEffect(() => { if (firstValue) setSelected(firstValue) }, [firstValue])

  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-overlay backdrop-blur-[2px] data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out" />
        <D.Content aria-describedby={undefined}
          className="fixed top-[12vh] left-1/2 z-50 w-[calc(100vw-24px)] max-w-[640px] -translate-x-1/2 overflow-hidden rounded-dialog border border-border bg-surface-raised shadow-dialog outline-none data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out">
          <D.Title className="sr-only">Command menu</D.Title>
          <Command shouldFilter={false} loop label="Command menu" value={selected} onValueChange={setSelected}
            onKeyDown={e => { if (e.key === 'Backspace' && !query && page !== 'root') { e.preventDefault(); setPage('root') } }}>
            <CommandInput
              value={query}
              onValueChange={setQuery}
              autoFocus
              placeholder={page === 'stores' ? 'Search stores…' : page === 'theme' ? 'Choose a theme…' : isStore ? 'Search products, orders, customers — or type a command' : 'Type a command or search…'}
              icon={page !== 'root' ? <button type="button" aria-label="Back" onClick={() => setPage('root')} className="grid size-5 place-items-center rounded hover:bg-surface-active"><ArrowLeft /></button> : <Search />}
              end={searching ? <Spinner size={14} className="text-text-subtle" /> : <Kbd>Esc</Kbd>}
            />
            <CommandList className="max-h-[min(62vh,460px)]">
              {page === 'stores' && (
                <CommandGroup heading="Stores">
                  {storeMatches.map(s => (
                    <CommandItem key={s.id} value={`store:${s.id}`} icon={<Avatar name={s.name} size={20} square />} hint={s.host}
                      end={<>{s.status !== 'active' && <StatusBadge status={s.status} size="sm" />}{s.id === storeId && <Check className="text-primary-strong" />}</>}
                      onSelect={() => run(() => { void switchStore(s.id) })}>
                      {s.name}
                    </CommandItem>
                  ))}
                  {!storeMatches.length && <CommandEmpty>No stores match.</CommandEmpty>}
                </CommandGroup>
              )}
              {page === 'theme' && (
                <CommandGroup heading="Theme">
                  {([['light', 'Light', <Sun key="s" />], ['dark', 'Dark', <Moon key="m" />], ['system', 'System', <Monitor key="c" />]] as const).map(([v, label, icon]) => (
                    <CommandItem key={v} value={`theme:${v}`} icon={icon} end={theme.preference === v ? <Check className="text-primary-strong" /> : undefined}
                      onSelect={() => run(() => setThemePreference(v))}>{label}</CommandItem>
                  ))}
                </CommandGroup>
              )}
              {page === 'root' && (
                <>
                  {nothing && <div className="px-3 py-10 text-center text-base text-text-muted">No results for “{query.trim()}”</div>}
                  {hits.length > 0 && (
                    <CommandGroup heading="Search results">
                      {hits.map(h => (
                        <CommandItem key={`${h.kind}:${h.id}`} value={`${h.kind}:${h.id}`} onSelect={() => nav(h.to)} hint={h.subtitle}
                          icon={h.kind === 'product' ? (h.image ? <img src={h.image} alt="" className="size-5 rounded object-cover" /> : <Package />) : h.kind === 'collection' ? <FolderOpen /> : h.kind === 'order' ? <Receipt /> : <User />}
                          end={<>{h.amount !== undefined && h.currency && <span className="text-sm text-text-subtle tabular">{money(h.amount, h.currency)}</span>}{h.status && <StatusBadge status={h.status} size="sm" />}</>}>
                          {h.title}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {searchFailed && <p className="px-3 py-2 text-sm text-text-muted">Search is unavailable right now.</p>}
                  {ai && (
                    <>
                      <AskAiCommand query={query} onClose={close} />
                    </>
                  )}
                  {!query && recents.length > 0 && (
                    <CommandGroup heading="Recent">
                      {recents.slice(0, 5).map(r => (
                        <CommandItem key={r.path} value={`recent:${r.path}`} icon={<Clock />} hint={(s => (s && s !== r.title ? s : undefined))(sectionFor(r.path.split('?')[0]))} onSelect={() => nav(r.path)}>
                          {r.title}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {actions.length > 0 && (
                    <CommandGroup heading="Actions">
                      {actions.map(i => <CommandItem key={i.id} value={i.id} icon={i.icon} shortcut={i.shortcut} onSelect={i.onSelect}>{i.label}</CommandItem>)}
                    </CommandGroup>
                  )}
                  {navItems.length > 0 && (
                    <>
                      {actions.length > 0 && <CommandSeparator />}
                      <CommandGroup heading="Go to">
                        {navItems.map(i => <CommandItem key={i.id} value={i.id} icon={i.icon} hint={i.hint} onSelect={i.onSelect}>{i.label}</CommandItem>)}
                      </CommandGroup>
                    </>
                  )}
                </>
              )}
            </CommandList>
            <div className={cn('flex items-center gap-4 border-t border-border bg-surface-sunken/60 px-3 py-2 text-xs text-text-subtle')}>
              <span className="inline-flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> to navigate</span>
              <span className="inline-flex items-center gap-1"><Kbd><CornerDownLeft className="size-3" /></Kbd> to open</span>
              {page !== 'root' && <span className="inline-flex items-center gap-1"><Kbd>⌫</Kbd> back</span>}
              <span className="ml-auto hidden sm:inline">{store && isStore ? store.name : kind === 'platform' ? 'Platform' : ''}</span>
            </div>
          </Command>
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}
